// src/analytics/cookieConsent.js

import { ensurePixelLoaded, disablePixel } from "./metaPixel";

const LS_KEY = "pwp_cookie_consent_v1";
const EVT_OPEN = "pwp_open_cookie_settings";
const EVT_CHANGED = "pwp_cookie_consent_changed";

// ✅ Your GA Measurement ID
// Option A: keep hardcoded (simple + safe for launch)
const GA_MEASUREMENT_ID = "G-TZC5XT1878";

// Option B (optional later): set REACT_APP_GA_MEASUREMENT_ID in env and use that
// const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "G-TZC5XT1878";

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getConsentState() {
  if (typeof window === "undefined") return { analytics: "not_set" };

  const raw = window.localStorage.getItem(LS_KEY);
  const data = raw ? safeParse(raw) : null;

  // analytics: "granted" | "denied" | "not_set"
  return {
    analytics: data?.analytics === "granted" ? "granted"
      : data?.analytics === "denied" ? "denied"
      : "not_set",
  };
}

export function shouldShowBanner() {
  const c = getConsentState();
  return c.analytics === "not_set";
}

function setConsent(analyticsValue) {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(
    LS_KEY,
    JSON.stringify({ analytics: analyticsValue, ts: Date.now() })
  );

  // Notify any listeners (banner)
  window.dispatchEvent(new CustomEvent(EVT_CHANGED, { detail: { analytics: analyticsValue } }));
}

function ensureGtagLoaded() {
  if (typeof window === "undefined") return;

  // Already loaded
  if (typeof window.gtag === "function") return;

  // Create dataLayer + gtag shim
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  // Load gtag.js
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    GA_MEASUREMENT_ID
  )}`;
  document.head.appendChild(script);

  // Init
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false, // we manually send SPA page_view events
  });
}

export function acceptAnalytics() {
  setConsent("granted");
  ensureGtagLoaded();
  ensurePixelLoaded(); // ← META PIXEL ADDED
}

export function rejectAnalytics() {
  // If rejected, we simply don't load GA.
  // (If GA had already loaded earlier, we can also disable it)
  setConsent("denied");

  if (typeof window !== "undefined") {
    // Best-effort disable if previously loaded
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  }
  disablePixel(); // ← META PIXEL ADDED
}

// ✅ Footer "Cookie Settings" button calls this.
// It triggers the banner to open again.
export function openCookieSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVT_OPEN));
}

// ✅ Banner subscribes to open + changed events
export function subscribeCookieUIEvents({ onOpen, onChange }) {
  if (typeof window === "undefined") return () => {};

  const openHandler = () => onOpen?.();
  const changeHandler = (e) => onChange?.(e?.detail);

  window.addEventListener(EVT_OPEN, openHandler);
  window.addEventListener(EVT_CHANGED, changeHandler);

  return () => {
    window.removeEventListener(EVT_OPEN, openHandler);
    window.removeEventListener(EVT_CHANGED, changeHandler);
  };
}

// ✅ On app start, if user already accepted earlier, auto-load GA silently.
export function maybeAutoLoadAnalytics() {
  const c = getConsentState();
  if (c.analytics === "granted") {
    ensureGtagLoaded();
    ensurePixelLoaded(); // ← META PIXEL ADDED
  }
}